import type { OutlookContext, NormalizedAttendee } from "./types";
import { findIana } from "windows-iana";

interface EmailAddressLike {
  emailAddress?: string | null;
  displayName?: string | null;
}

interface RecipientCollectionLike {
  getAsync: (callback: (result: Office.AsyncResult<EmailAddressLike[]>) => void) => void;
}

type RecipientSource = EmailAddressLike[] | RecipientCollectionLike | undefined | null;

type AttendeeRole = NormalizedAttendee["role"];

const BODY_CHAR_LIMIT = 5000;

function hasGetAsync(source: RecipientSource): source is RecipientCollectionLike {
  return !!source && typeof (source as RecipientCollectionLike).getAsync === "function";
}

function runAsync<T>(
  executor: (callback: (result: Office.AsyncResult<T>) => void) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    executor((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value as T);
      } else {
        reject(result.error ?? new Error("Async call failed"));
      }
    });
  });
}

async function readStringValue(
  value:
    | string
    | { getAsync: (callback: (result: Office.AsyncResult<string>) => void) => void }
    | undefined
    | null
): Promise<string> {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value.getAsync === "function") {
    return runAsync<string>((callback) => value.getAsync(callback));
  }
  return "";
}

async function readBody(
  item:
    | Office.MessageCompose
    | Office.MessageRead
    | Office.AppointmentCompose
    | Office.AppointmentRead
): Promise<string> {
  if (!item.body || typeof item.body.getAsync !== "function") {
    return "";
  }
  try {
    const text = await new Promise<string>((resolve, reject) => {
      item.body.getAsync("text", (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value ?? "");
        } else {
          reject(result.error ?? new Error("Unable to read body"));
        }
      });
    });
    return text ?? "";
  } catch (err) {
    console.error("[OutlookContext] body read failed", err);
    return "";
  }
}

async function readRecipients(source: RecipientSource): Promise<EmailAddressLike[]> {
  if (!source) {
    return [];
  }
  if (Array.isArray(source)) {
    return source;
  }
  if (hasGetAsync(source)) {
    try {
      return await runAsync<EmailAddressLike[]>((callback) => source.getAsync(callback));
    } catch (err) {
      console.error("[OutlookContext] recipient read failed", err);
      return [];
    }
  }
  return [];
}

interface RawAttendee extends EmailAddressLike {
  role: AttendeeRole;
}

export function normalizeAttendees(
  raw: RawAttendee[],
  currentUserEmail?: string | null,
  currentUserName?: string | null
): NormalizedAttendee[] {
  const map = new Map<string, NormalizedAttendee>();
  const push = (entry: RawAttendee): void => {
    const email = entry.emailAddress?.trim();
    if (!email) {
      return;
    }
    const normalizedEmail = email.toLowerCase();
    if (map.has(normalizedEmail)) {
      return;
    }
    map.set(normalizedEmail, {
      email: normalizedEmail,
      displayName: entry.displayName?.trim() || email,
      role: entry.role,
    });
  };

  raw.forEach(push);

  if (currentUserEmail) {
    push({
      emailAddress: currentUserEmail,
      displayName: currentUserName ?? currentUserEmail,
      role: "self",
    });
  }

  return Array.from(map.values());
}

function detectItemType(item: Office.Item | any): "message" | "event" {
  return item?.itemType === Office.MailboxEnums.ItemType.Appointment ? "event" : "message";
}

function getMailboxTimezone(): string {
  const windowsTz = Office.context.mailbox.userProfile?.timeZone?.trim();
  if (!windowsTz) {
    return "UTC";
  }
  const iana = findIana(windowsTz);
  if (!iana || iana.length === 0) {
    console.warn(`[OutlookContext] Could not map Windows timezone "${windowsTz}" to IANA. Falling back to UTC.`);
    return "UTC";
  }
  return iana[0];
}

export async function getOutlookContext(): Promise<OutlookContext> {
  const item = Office.context.mailbox.item;
  const subject = (await readStringValue((item as any).subject)).trim();
  const body = (await readBody(item)).slice(0, BODY_CHAR_LIMIT);
  const currentUserEmail = Office.context.mailbox.userProfile?.emailAddress ?? null;
  const currentUserName = Office.context.mailbox.userProfile?.displayName ?? null;

  const rawAttendees: RawAttendee[] = [];

  const itemType = detectItemType(item);

  if (itemType === "message") {
    const sender =
      (item as Office.MessageCompose).from ??
      (item as Office.MessageRead).sender ??
      (item as any).from;
    if (sender) {
      rawAttendees.push({ ...sender, role: "sender" });
    }

    const toRecipients = await readRecipients((item as any).to || (item as any).requiredAttendees);
    toRecipients.forEach((r) => rawAttendees.push({ ...r, role: "required" }));

    const ccRecipients = await readRecipients((item as any).cc || (item as any).optionalAttendees);
    ccRecipients.forEach((r) => rawAttendees.push({ ...r, role: "optional" }));
  } else {
    const organizer =
      (item as Office.AppointmentCompose).organizer ?? (item as Office.AppointmentRead).organizer;
    if (organizer) {
      rawAttendees.push({ ...organizer, role: "organizer" });
    }
    const required = await readRecipients(
      (item as Office.AppointmentCompose).requiredAttendees ||
        (item as Office.AppointmentRead).requiredAttendees
    );
    required.forEach((r) => rawAttendees.push({ ...r, role: "required" }));
    const optional = await readRecipients(
      (item as Office.AppointmentCompose).optionalAttendees ||
        (item as Office.AppointmentRead).optionalAttendees
    );
    optional.forEach((r) => rawAttendees.push({ ...r, role: "optional" }));
  }

  const attendees = normalizeAttendees(rawAttendees, currentUserEmail, currentUserName);

  return {
    itemType,
    subject,
    bodyPreview: body,
    attendees,
    timezone: getMailboxTimezone(),
  };
}
